<template>
    <LoadingModal v-if="loading" header="Add entity" open @close="close" />
    <AddIndividualEntityModal
        v-else-if="opened"
        :custom-properties="sortedCustomProperties"
        :requirement-types="nonMultiInstanceRequirementTypes"
        :requirement-definitions="requirementDefinitions"
        :saving="saving"
        :alert-config="alertConfig"
        @close="close"
        @submit="addInsured"
    />
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { AlertType } from '@evidentid/dashboard-commons/components/Alert/types';
    import { OperationError } from '@evidentid/rpweb-api-client/models';
    import { TprmRequirementDefinition } from '@evidentid/tprm-portal-lib/models/common';
    import { CustomProperty, EntityInput, UiConfig } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import LoadingModal from '@/components/loading-modal/LoadingModal.vue';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import AddIndividualEntityModal
        from '@/modules/entity-management/components/AddIndividualEntityModal/AddIndividualEntityModal.vue';
    import {
        DUPLICATE_CONTACT_EMAIL_MESSAGE,
    } from '@/modules/entity-management/constants/entityMessages.constant';
    import { BatchAddEntitiesStatus, useEntityManagementStore } from '@/modules/entity-management/store';
    import { getSortedCustomProperties } from '@/utils/get-sorted-custom-properties/getSortedCustomProperties';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    @Component({
        components: { AddIndividualEntityModal, LoadingModal },
        emits: [ 'abort', 'finish' ],
    })

    export default class AddIndividualEntityProcedure extends Vue {
        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String, default: '' })
        private rpName!: string;
        private opened: boolean = true;
        private saving: boolean = false;
        private alertConfig: { type: AlertType, title: string } | null = null;
        private needCollectEvidence: boolean = false;

        @Watch('addEntitiesStatus')
        private async onAddEntitiesStatusChange(addEntitiesStatus: BatchAddEntitiesStatus) {
            this.saving = addEntitiesStatus.status === OperationStatus.loading;
            if (addEntitiesStatus.status === OperationStatus.success) {
                if (addEntitiesStatus.failures.length === 0) {
                    this.$emit('finish', this.needCollectEvidence);
                } else if (addEntitiesStatus.failures[0].error.error === OperationError.keyConflict) {
                    this.alertConfig = { type: 'danger', title: DUPLICATE_CONTACT_EMAIL_MESSAGE };
                } else {
                    // Every other OperationError lands here so the modal always says something
                    // rather than sitting silent after a failed save.
                    this.alertConfig = {
                        type: 'danger',
                        title: 'Cannot connect the entity, an unknown error occurred.',
                    };
                }
            } else if (addEntitiesStatus.status === OperationStatus.error) {
                this.alertConfig = {
                    type: 'danger',
                    title: 'Sorry, something went wrong during add entities. Please try again later.',
                };
            }
        }

        private get addEntitiesStatus(): BatchAddEntitiesStatus {
            return useEntityManagementStore().addEntitiesStatus[this.rpName] || {
                status: OperationStatus.uninitialized,
                count: 0,
                successCount: 0,
                successes: [],
                failures: [],
            };
        }

        private get customPropertiesStatus(): { status: OperationStatus, list: CustomProperty[] } {
            return useDashboardStore().customProperties[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get customPropertiesDisplayStatus(): { status: OperationStatus, data: UiConfig } {
            const config = useDashboardStore().rpConfig[this.rpName];
            return {
                status: config?.status ?? OperationStatus.uninitialized,
                data: {
                    customPropertiesOrder: config?.customPropertiesOrder ?? [],
                    entitiesTableCustomProperties: config?.entitiesTableCustomProperties ?? [],
                },
            };
        }

        private get sortedCustomProperties(): CustomProperty[] {
            return getSortedCustomProperties(
                this.customPropertiesStatus.list,
                this.customPropertiesDisplayStatus.data.customPropertiesOrder,
            );
        }

        private get clientRequirementTypesStatus(): { status: OperationStatus, list: string[] } {
            return useDashboardStore().clientRequirementTypes[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get requirementDefinitions(): Record<string, TprmRequirementDefinition> {
            return useDashboardStore().tprmRequirementsDefinitions || [];
        }

        private get nonMultiInstanceRequirementTypes(): string[] {
            return this.clientRequirementTypesStatus.list.filter(
                (type) => !this.requirementDefinitions[type]?.multiInstance,
            );
        }

        private get loading(): boolean {
            return this.customPropertiesStatus.status === OperationStatus.loading
                || this.clientRequirementTypesStatus.status === OperationStatus.loading;
        }

        private mounted() {
            if (this.customPropertiesStatus.status !== OperationStatus.loading) {
                useDashboardStore().loadCustomProperties({ rpName: this.rpName });
            }
            if (this.clientRequirementTypesStatus.status !== OperationStatus.loading) {
                useDashboardStore().loadClientRequirementTypes({ rpName: this.rpName });
            }
            if (this.customPropertiesDisplayStatus.status !== OperationStatus.loading) {
                useDashboardStore().loadRpConfig({ rpName: this.rpName });
            }
        }

        private close(): void {
            this.opened = false;
            useEntityManagementStore().clearAddEntitiesStatus({ rpName: this.rpName });
            this.$emit('abort');
        }

        private addInsured(entity: EntityInput, hasPastExpiration: boolean) {
            this.alertConfig = null;
            this.needCollectEvidence = hasPastExpiration;
            useEntityManagementStore().addEntities({ rpName: this.rpName, entities: [ entity ] });
        }
    }
</script>
